/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { lAC_ITSUpdateEcomCheckoutAsync } from '../custom-actions/DataActionExtension.g';
import * as Entities from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import {
    IITSIntegrationCartLine,
    IITSUpdateEcomCheckoutRequest,
    IITSUpdateEcomCheckoutResponse,
    ILAC_AuthToken,
    ILAC_EcomPaymentSelection
} from '../custom-actions/DataServiceEntities.g';
import { refreshToken } from '../utils/authService';
import { IStoreRequestFailure, readStoreRequestFailure, storeRequestSessionExpired } from '../utils/storerequestfailure';

/**
 * UpdateCartLines Input Action
 */

export class UpdateCartLinesPOSInput implements Msdyn365.IActionInput {
    public checkoutData: {
        CartId?: string;
        StoreCartId?: string;
        ChannelId?: number;
        AddCartLines?: IITSIntegrationCartLine[];
        RemoveCartLines?: IITSIntegrationCartLine[];
        UpdateCartLines?: IITSIntegrationCartLine[];
        ExtensionProperties?: Entities.CommerceProperty[];
        AttributeValues?: Entities.AttributeTextValue[];
    };
    public authTokenCookie: ILAC_AuthToken;
    public customerId: string;
    public paymentSelection: ILAC_EcomPaymentSelection;

    constructor(
        {
            CartId,
            StoreCartId,
            ChannelId,
            AddCartLines = [],
            RemoveCartLines = [],
            UpdateCartLines = [],
            ExtensionProperties = [],
            AttributeValues = []
        }: IITSUpdateEcomCheckoutRequest,
        authTokenCookie: ILAC_AuthToken,
        customerId: string,
        paymentSelection: ILAC_EcomPaymentSelection
    ) {
        if (!ChannelId || isNaN(Number(ChannelId))) {
            throw new Error('ChannelId must be a valid number');
        }
        this.checkoutData = {
            CartId: CartId,
            StoreCartId: StoreCartId || undefined,
            ChannelId: Number(ChannelId),
            AddCartLines: AddCartLines,
            RemoveCartLines: RemoveCartLines,
            UpdateCartLines: UpdateCartLines,
            ExtensionProperties: ExtensionProperties,
            AttributeValues: AttributeValues
        };
        this.authTokenCookie = authTokenCookie;
        this.customerId = customerId;
        this.paymentSelection = paymentSelection;
    }

    // a cache object type and an appropriate cache key
    public getCacheKey = () => `ITSUPDATECARTLINE`;
    public getCacheObjectType = () => 'ITSUPDATECARTLINE';
    public dataCacheType = (): Msdyn365.CacheType => 'none';
}

async function updateEcomCheckoutAsync(
    input: UpdateCartLinesPOSInput,
    ctx: Msdyn365.IActionContext
): Promise<IITSUpdateEcomCheckoutResponse | undefined> {
    const authToken = input.authTokenCookie;
    let failure: IStoreRequestFailure | undefined;

    if (authToken && authToken.IntuitDomeAccountId && authToken.Token && authToken.RefreshToken) {
        try {
            const result = await lAC_ITSUpdateEcomCheckoutAsync(
                { callerContext: ctx },
                authToken,
                input.customerId,
                input.checkoutData,
                input.paymentSelection
            );
            if (result) {
                return result;
            }
        } catch (error) {
            console.log('Error resuming cart', error);
            failure = readStoreRequestFailure(error);

            const actionError = error as {
                data?: {
                    ErrorResourceId?: string;
                };
            };

            const unAuthorized = actionError.data?.ErrorResourceId === '401';
            if (unAuthorized) {
                failure = undefined;
                const refreshedToken = await refreshToken(ctx);
                if (refreshedToken) {
                    try {
                        const result = await lAC_ITSUpdateEcomCheckoutAsync(
                            { callerContext: ctx },
                            refreshedToken,
                            input.customerId,
                            input.checkoutData,
                            input.paymentSelection
                        );
                        if (result) {
                            return result;
                        }
                    } catch (error) {
                        console.log('Error resuming cart', error);
                        failure = readStoreRequestFailure(error);
                    }
                }
            }
        }
    }
    return failure ?? storeRequestSessionExpired();
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<IITSUpdateEcomCheckoutResponse | undefined>>updateEcomCheckoutAsync,
    id: 'ITSUpdateCartLinePOS'
});
